import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface LiveModeConfig {
  minutes: number;
  label: string;
  refreshIntervalSeconds: number;
}

const DEFAULT_REFRESH_SECONDS = 60;

export const useLiveModeStore = defineStore('liveMode', () => {
  const isLiveMode = ref(false);
  const liveMinutes = ref(0);
  const liveLabel = ref('');
  const liveRefreshSeconds = ref(DEFAULT_REFRESH_SECONDS);
  const liveCountdown = ref(DEFAULT_REFRESH_SECONDS);

  // Incremented on each auto-refresh tick; RbzFilter watches this to fetch data.
  const refreshTrigger = ref(0);

  let _timerId: ReturnType<typeof setInterval> | null = null;
  let _mountedFilterCount = 0;

  const _tick = () => {
    liveCountdown.value -= 1;
    if (liveCountdown.value <= 0) {
      liveCountdown.value = liveRefreshSeconds.value;
      refreshTrigger.value++;
    }
  };

  const _startTimer = () => {
    if (_timerId !== null) return;
    _timerId = setInterval(_tick, 1000);
  };

  const _stopTimer = () => {
    if (_timerId !== null) {
      clearInterval(_timerId);
      _timerId = null;
    }
  };

  const _handleVisibilityChange = () => {
    if (!isLiveMode.value) return;
    if (document.hidden) {
      _stopTimer();
    } else if (_mountedFilterCount > 0) {
      _startTimer();
    }
  };

  const startLiveMode = (config: LiveModeConfig) => {
    const safeInterval =
      config.refreshIntervalSeconds > 0 ? config.refreshIntervalSeconds : DEFAULT_REFRESH_SECONDS;
    isLiveMode.value = true;
    liveMinutes.value = config.minutes;
    liveLabel.value = config.label;
    liveRefreshSeconds.value = safeInterval;
    liveCountdown.value = safeInterval;

    if (_mountedFilterCount > 0 && !document.hidden) {
      _startTimer();
    }

    // Remove before re-adding to avoid stacking listeners when live config is updated
    document.removeEventListener('visibilitychange', _handleVisibilityChange);
    document.addEventListener('visibilitychange', _handleVisibilityChange);
  };

  const stopLiveMode = () => {
    isLiveMode.value = false;
    liveMinutes.value = 0;
    liveLabel.value = '';
    liveRefreshSeconds.value = DEFAULT_REFRESH_SECONDS;
    liveCountdown.value = DEFAULT_REFRESH_SECONDS;
    _stopTimer();
    document.removeEventListener('visibilitychange', _handleVisibilityChange);
  };

  // Called by RbzFilter on mount; resumes the timer if live mode is already active.
  const registerFilter = () => {
    _mountedFilterCount++;
    if (isLiveMode.value && _mountedFilterCount === 1 && !document.hidden) {
      _startTimer();
    }
  };

  // Called by RbzFilter on unmount; pauses the timer when no filter is on screen.
  const unregisterFilter = () => {
    _mountedFilterCount = Math.max(0, _mountedFilterCount - 1);
    if (_mountedFilterCount === 0) {
      _stopTimer();
    }
  };

  return {
    isLiveMode,
    liveMinutes,
    liveLabel,
    liveRefreshSeconds,
    liveCountdown,
    refreshTrigger,
    startLiveMode,
    stopLiveMode,
    registerFilter,
    unregisterFilter,
  };
});
